import { Component, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/authentication/auth.service';
import { Usuario } from 'src/app/authentication/interfaces/interfaces';
import { ClinicalOrder, MedicalRecordPayload } from 'src/app/interfaces/gestion-clinica/medical-record.interface';
import { MedicalRecordsService } from 'src/app/services/gestion-clinica/medical-records.service';
import { buildHistoriaClinicaHtml } from '../shared/historia-clinica-print';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-crear-historia-clinica',
  templateUrl: './crear-historia-clinica.component.html',
  styleUrls: ['./crear-historia-clinica.component.scss'],
  standalone: false
})
export class CrearHistoriaClinicaComponent implements OnInit {
  form: FormGroup;
  order?: ClinicalOrder | any;
  loading = false;
  saving = false;
  loadingIdentification = false;
  isEditMode = false;
  recordId?: string;
  sectionCollapsed: { [key: string]: boolean } = {
    admission: false,
    consultation: true,
    personalHistory: true,
    currentIllness: true,
    systemsReview: true,
    vitalSigns: true,
    physicalExam: true,
    diagnoses: true,
    treatmentPlan: true,
    professionalData: true
  };
  private usuario: Usuario;
  private readonly requiredFieldLabels: { [key: string]: { label: string; section: string } } = {
    'admission.health_facility': { label: 'Establecimiento de salud', section: 'admission' },
    'admission.clinical_record_number': { label: 'N° Historia Clínica / Cédula', section: 'admission' },
    'admission.last_names': { label: 'Apellidos', section: 'admission' },
    'admission.sex': { label: 'Sexo', section: 'admission' },
    'consultation_reason.main_reason': { label: 'Motivo principal', section: 'consultation' },
    'current_illness.start_evolution': { label: 'Inicio y evolución', section: 'currentIllness' },
    'professional_data.date': { label: 'Fecha del profesional', section: 'professionalData' },
    'professional_data.start_time': { label: 'Hora inicio', section: 'professionalData' },
    'professional_data.doctor_name': { label: 'Nombre del médico tratante', section: 'professionalData' }
  };
  private readonly requiredSections = ['admission', 'consultation', 'currentIllness', 'professionalData'];
  readonly requiredSectionCards = [
    { key: 'admission', label: 'Admisión' },
    { key: 'consultation', label: 'Motivo' },
    { key: 'currentIllness', label: 'Enfermedad actual' },
    { key: 'professionalData', label: 'Profesional' }
  ];

  systems = [
    'Órganos de los Sentidos',
    'Respiratorio',
    'Cardiovascular',
    'Digestivo',
    'Genito-urinario',
    'Músculo-esquelético',
    'Endocrino',
    'Nervioso / Mental'
  ];

  regions = [
    'Apariencia / Facies',
    'Cabeza y Cuello',
    'Tórax Cardíaco',
    'Tórax Pulmonar',
    'Abdomen',
    'Región Lumbar',
    'Pelvis/Genitales',
    'Extremidades',
    'Neurológico'
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private medicalRecordsService: MedicalRecordsService
  ) {
    this.usuario = this.authService.loginUsuario;
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    const orderId = this.route.snapshot.paramMap.get('orderId');
    this.recordId = this.route.snapshot.paramMap.get('recordId') || undefined;
    if (this.recordId) {
      this.isEditMode = true;
      this.loadRecord(this.recordId);
      return;
    }
    if (orderId) this.loadOrder(orderId);
  }

  get diagnoses(): FormArray {
    return this.form.get('diagnoses') as FormArray;
  }

  get admissionControl(): AbstractControl {
    return this.form.get('admission') as AbstractControl;
  }

  get consultationReasonControl(): AbstractControl {
    return this.form.get('consultation_reason') as AbstractControl;
  }

  get professionalDataControl(): AbstractControl {
    return this.form.get('professional_data') as AbstractControl;
  }

  get secondaryReasons(): FormArray {
    return this.form.get('consultation_reason.secondary_reasons') as FormArray;
  }

  get systemsReview(): FormArray {
    return this.form.get('systems_review.items') as FormArray;
  }

  get physicalExam(): FormArray {
    return this.form.get('physical_exam.items') as FormArray;
  }

  addDiagnosis(): void {
    this.diagnoses.push(this.createDiagnosis());
  }

  removeDiagnosis(index: number): void {
    if (this.diagnoses.length > 1) this.diagnoses.removeAt(index);
  }

  consultarIdentificacion(): void {
    const documento = String(this.form.get('admission.clinical_record_number')?.value || '').trim();
    if (!documento) {
      Swal.fire('Historia Clínica', 'Ingrese un número de identificación.', 'warning');
      return;
    }

    this.loadingIdentification = true;
    this.medicalRecordsService.consultarIdentificacion(documento).subscribe({
      next: (resp: any) => {
        this.loadingIdentification = false;
        if (!resp?.resultado?.resultado || !resp?.datos) {
          Swal.fire('Historia Clínica', resp?.resultado?.mensaje || 'No se encontraron datos para la identificación.', 'warning');
          return;
        }
        this.patchIdentificationData(resp.datos);
      },
      error: (err: any) => {
        this.loadingIdentification = false;
        Swal.fire('Historia Clínica', err?.error?.message || err?.message || 'No se encontraron datos para la identificación.', 'warning');
      }
    });
  }

  toggleSection(section: string): void {
    this.sectionCollapsed[section] = !this.sectionCollapsed[section];
  }

  isSectionCollapsed(section: string): boolean {
    return !!this.sectionCollapsed[section];
  }

  isRequiredSection(section: string): boolean {
    return this.requiredSections.includes(section);
  }

  hasMissingRequiredInSection(section: string): boolean {
    return Object.keys(this.requiredFieldLabels)
      .filter((path) => this.requiredFieldLabels[path].section === section)
      .some((path) => {
        const control = this.form.get(path);
        return !!control && control.enabled && control.invalid && (control.touched || control.dirty);
      });
  }

  isRequiredSectionComplete(section: string): boolean {
    return Object.keys(this.requiredFieldLabels)
      .filter((path) => this.requiredFieldLabels[path].section === section)
      .every((path) => this.isRequiredControlComplete(path));
  }

  openSection(section: string): void {
    this.sectionCollapsed[section] = false;
  }

  get requiredCompleted(): number {
    return this.requiredSectionCards.filter((section) => this.isRequiredSectionComplete(section.key)).length;
  }

  get requiredTotal(): number {
    return this.requiredSectionCards.length;
  }

  get completionPercent(): number {
    if (!this.requiredTotal) return 0;
    return Math.round((this.requiredCompleted / this.requiredTotal) * 100);
  }

  private isRequiredControlComplete(path: string): boolean {
    const control = this.form.get(path);
    if (!control) return false;
    if (control.disabled) {
      const value = control.value;
      return value !== null && value !== undefined && String(value).trim() !== '';
    }
    return control.valid;
  }

  save(): void {
    if (!this.order) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const missingFields = this.getMissingRequiredFields();
      const firstSection = missingFields[0]?.section;
      if (firstSection) this.sectionCollapsed[firstSection] = false;
      Swal.fire({
        title: 'Historia Clínica',
        icon: 'warning',
        html: `
          <div class="text-start">
            <p>Complete los siguientes campos obligatorios:</p>
            <ul>${missingFields.map((item) => `<li>${item.label}</li>`).join('')}</ul>
          </div>
        `
      });
      return;
    }

    this.saving = true;
    const value = this.form.getRawValue();
    const payload: MedicalRecordPayload = {
      order_id: Number(this.order.order_id),
      patient_id: Number(this.order.patient_id),
      doctor_id: Number(this.order.doctor_id),
      branch_id: Number(this.order.branch_id || this.usuario.sucursal || 0) || undefined,
      created_by_user_id: Number(this.usuario.idUsuario || 0),
      admission: value.admission,
      consultation_reason: value.consultation_reason,
      personal_history: value.personal_history,
      current_illness: value.current_illness,
      systems_review: value.systems_review,
      vital_signs: value.vital_signs,
      physical_exam: value.physical_exam,
      diagnoses: value.diagnoses,
      treatment_plan: value.treatment_plan,
      professional_data: value.professional_data,
      requested_services: null
    };

    const request = this.isEditMode && this.recordId
      ? this.medicalRecordsService.updateRecord(this.recordId, payload)
      : this.medicalRecordsService.createRecord(payload);

    request.subscribe({
      next: () => {
        this.saving = false;
        const message = this.isEditMode ? 'Historia clínica actualizada correctamente.' : 'Historia clínica creada correctamente.';
        Swal.fire('Historia Clínica', message, 'success').then(() => {
          this.abrirPdfHistoriaClinica(true);
          this.volver();
        });
      },
      error: (err: any) => {
        this.saving = false;
        Swal.fire('Historia Clínica', err?.error?.message || err?.message || 'No se pudo guardar la historia clínica.', 'error');
      }
    });
  }

  previsualizarPdf(): void {
    this.abrirPdfHistoriaClinica(false);
  }

  private abrirPdfHistoriaClinica(permitirImpresion: boolean): void {
    if (!this.order) return;
    const printWindow = window.open('', '_blank', 'width=1000,height=1100');
    if (!printWindow) {
      Swal.fire('Historia Clínica', 'El navegador bloqueó la ventana emergente del PDF.', 'warning');
      return;
    }

    printWindow.document.write(this.buildPrintablePreview(permitirImpresion));
    printWindow.document.close();
    printWindow.focus();
  }

  solicitarServiciosOrden(): void {
    if (!this.order?.patient_id) {
      Swal.fire('Historia Clínica', 'No se encontró el paciente de la orden.', 'warning');
      return;
    }

    const urlTree = this.router.createUrlTree(['/sistema/crear-orden'], {
      queryParams: {
        paciente: this.order.patient_id,
        doctor: '7784',
        origen: 'historia-clinica',
        ordenOrigen: this.order.order_id
      }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  cancel(): void {
    this.volver();
  }

  private volver(): void {
    const target = this.isEditMode
      ? '/sistema/gestion-clinica/historial'
      : '/sistema/gestion-clinica/historia-clinica';
    this.router.navigate([target]);
  }

  private loadOrder(orderId: string): void {
    this.loading = true;
    this.medicalRecordsService.orderContext(orderId).subscribe({
      next: (resp: any) => {
        this.loading = false;
        this.order = resp?.row;
        this.patchAdmission();
        this.consultarIdentificacionSilenciosa();
      },
      error: (err: any) => {
        this.loading = false;
        Swal.fire('Gestión Clínica', err?.error?.message || err?.message || 'No se pudo cargar la orden.', 'error');
        this.cancel();
      }
    });
  }

  private loadRecord(recordId: string): void {
    this.loading = true;
    this.medicalRecordsService.record(recordId).subscribe({
      next: (resp: any) => {
      this.loading = false;
      const record = resp?.row;
      this.order = {
        order_id: record.order_id,
        order_number: record.order_number,
        invoice_number: record.invoice_number,
        patient_id: record.patient_id,
        patient_name: record.patient_name,
        patient_document: record.patient_document,
        doctor_id: record.doctor_id,
        doctor_name: record.order_doctor_name,
        branch_id: record.branch_id,
        branch_name: record.branch_name
      };
      this.form.patchValue({
        admission: record.admission || {},
        consultation_reason: record.consultation_reason || {},
        personal_history: record.personal_history || {},
        current_illness: record.current_illness || {},
        vital_signs: record.vital_signs || {},
        treatment_plan: record.treatment_plan || {},
        professional_data: record.professional_data || {}
      });
      this.form.get('admission.health_facility')?.setValue(this.buildHealthFacilityName(record.branch_name || ''));
      this.patchFormArray('diagnoses', record.diagnoses || []);
      this.patchNestedArray('systems_review', 'items', record.systems_review?.items || []);
      this.patchNestedArray('physical_exam', 'items', record.physical_exam?.items || []);
      },
      error: (err: any) => {
        this.loading = false;
        Swal.fire('Gestión Clínica', err?.error?.message || err?.message || 'No se pudo cargar la historia clínica.', 'error');
        this.router.navigate(['/sistema/gestion-clinica/historial']);
      }
    });
  }

  private patchFormArray(arrayName: string, values: any[]): void {
    const array = this.form.get(arrayName) as FormArray;
    while (array.length) array.removeAt(0);
    values.forEach((value) => {
      const group = this.createDiagnosis();
      group.patchValue(value || {});
      array.push(group);
    });
    if (!array.length) array.push(this.createDiagnosis());
  }

  private patchNestedArray(groupName: string, arrayName: string, values: any[]): void {
    const array = this.form.get(`${groupName}.${arrayName}`) as FormArray;
    if (!array || !values.length) return;
    values.forEach((value, index) => {
      if (array.at(index)) array.at(index).patchValue(value || {});
    });
  }

  private patchAdmission(): void {
    const birthDate = this.order?.patient_birth_date || this.order?.FEC_CLI;
    this.form.patchValue({
      admission: {
        health_facility: this.buildHealthFacilityName(this.order?.branch_name || this.usuario.nombreSucursal || ''),
        clinical_record_number: this.order?.patient_document || this.order?.CED_CLI || '',
        last_names: this.order?.patient_name || this.order?.NOM_CLI || '',
        birth_date: birthDate || '',
        phone: this.order?.patient_mobile || this.order?.CEL_CLI || this.order?.TEL_CLI || '',
        email: this.order?.patient_email || this.order?.EMA_CLI || '',
        address: this.order?.patient_address || this.order?.DIR_CLI || ''
      },
      professional_data: {
        date: new Date().toISOString().substring(0, 10),
        start_time: new Date().toTimeString().substring(0, 5),
        doctor_name: this.usuario.nombre || '',
        msp_code: ''
      }
    });
  }

  private consultarIdentificacionSilenciosa(): void {
    const documento = String(this.form.get('admission.clinical_record_number')?.value || '').trim();
    if (!documento || documento.length < 10) return;

    this.loadingIdentification = true;
    this.medicalRecordsService.consultarIdentificacion(documento).subscribe({
      next: (resp: any) => {
        this.loadingIdentification = false;
        if (resp?.resultado?.resultado && resp?.datos) {
          this.patchIdentificationData(resp.datos);
        }
      },
      error: () => {
        this.loadingIdentification = false;
      }
    });
  }

  private patchIdentificationData(datos: any): void {
    const fullName = String(datos.Nombre || '').trim();
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    const lastNames = nameParts.slice(0, 2).join(' ');
    const names = nameParts.slice(2).join(' ');
    const street = String(datos.Calle || '').trim();
    const houseNumber = String(datos.NumeroCasa || '').trim();

    const birthDate = this.toInputDate(datos.FechaNacimiento);
    this.form.patchValue({
      admission: {
        clinical_record_number: datos.NUI || this.form.get('admission.clinical_record_number')?.value,
        last_names: lastNames || fullName,
        names,
        birth_date: birthDate,
        age: this.calculateAge(birthDate),
        nationality: this.toTitleCase(datos.Nacionalidad),
        sex: this.normalizeSex(datos.Sexo),
        gender_identity: this.toTitleCase(datos.Genero),
        marital_status: this.normalizeCivilStatus(datos.EstadoCivil),
        residence: this.toTitleCase(datos.Domicilio),
        address: [this.toTitleCase(street), houseNumber && houseNumber !== 'SN' ? houseNumber : ''].filter(Boolean).join(' '),
        education: this.toTitleCase(datos.Instruccion),
        occupation: this.toTitleCase(datos.Profesion)
      }
    });
  }

  private toInputDate(value: string): string {
    const raw = String(value || '').trim();
    const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return '';
    return `${match[3]}-${match[2]}-${match[1]}`;
  }

  calculateAgeFromAdmission(): void {
    const birthDate = String(this.form.get('admission.birth_date')?.value || '');
    this.form.get('admission.age')?.setValue(this.calculateAge(birthDate));
  }

  private calculateAge(birthDate: string): string {
    if (!birthDate) return '';
    const date = new Date(`${birthDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) return '';

    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age -= 1;
    }
    return age >= 0 ? String(age) : '';
  }

  private buildPrintablePreview(permitirImpresion: boolean): string {
    const value = this.form.getRawValue();
    const order = this.order || {};
    return buildHistoriaClinicaHtml(
      {
        ...value,
        order: {
          order_number: order.order_number,
          invoice_number: order.invoice_number,
          branch_name: order.branch_name
        }
      },
      { permitirImpresion }
    );
  }

  private normalizeSex(value: string): string {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'HOMBRE' || normalized === 'MASCULINO') return 'Hombre';
    if (normalized === 'MUJER' || normalized === 'FEMENINO') return 'Mujer';
    return '';
  }

  private normalizeCivilStatus(value: string): string {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'SOLTERO' || normalized === 'SOLTERA') return 'Soltero';
    if (normalized === 'CASADO' || normalized === 'CASADA') return 'Casado';
    if (normalized === 'DIVORCIADO' || normalized === 'DIVORCIADA') return 'Divorciado';
    if (normalized === 'VIUDO' || normalized === 'VIUDA') return 'Viudo';
    if (normalized === 'UNION LIBRE' || normalized === 'UNIÓN LIBRE') return 'Unión Libre';
    return '';
  }

  private toTitleCase(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
  }

  private buildHealthFacilityName(branchName: string): string {
    const branch = String(branchName || '').trim();
    return branch ? `C&M IMAGENES MEDICINA Y SALUD - ${branch}` : 'C&M IMAGENES MEDICINA Y SALUD';
  }

  private getMissingRequiredFields(): Array<{ label: string; section: string }> {
    return Object.keys(this.requiredFieldLabels)
      .filter((path) => this.form.get(path)?.invalid)
      .map((path) => this.requiredFieldLabels[path]);
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      admission: this.fb.group({
        health_facility: [{ value: '', disabled: true }, Validators.required],
        clinical_record_number: ['', Validators.required],
        last_names: ['', Validators.required],
        names: [''],
        birth_date: [''],
        age: [''],
        nationality: [''],
        sex: ['', Validators.required],
        gender_identity: [''],
        marital_status: [''],
        ethnicity: [''],
        ethnicity_other: [''],
        residence: [''],
        address: [''],
        phone: [''],
        email: [''],
        education: [''],
        occupation: [''],
        blood_type: [''],
        disability: [false],
        disability_percent: [''],
        emergency_name: [''],
        emergency_relationship: [''],
        emergency_phone: ['']
      }),
      consultation_reason: this.fb.group({
        main_reason: ['', Validators.required],
        evolution_time_unit: [''],
        exact_time: [''],
        attention_classification: [''],
        secondary_reasons: this.fb.array([this.fb.control(''), this.fb.control('')])
      }),
      personal_history: this.fb.group({
        clinical_diseases: [''],
        previous_surgeries: [''],
        allergies: [''],
        transfusions: [''],
        tobacco: [false],
        tobacco_amount: [''],
        alcohol: [false],
        drugs: [false],
        physical_activity: [''],
        family_history: [''],
        menarche: [''],
        fum: [''],
        cycles: [''],
        sexual_life: [''],
        pregnancies: [''],
        births: [''],
        abortions: [''],
        cesareans: [''],
        living_children: [''],
        pap: ['']
      }),
      current_illness: this.fb.group({
        start_evolution: ['', Validators.required],
        current_state: [''],
        pain_intensity: [''],
        associated_symptoms: ['']
      }),
      systems_review: this.fb.group({
        items: this.fb.array(this.systems.map(system => this.fb.group({
          system: [system],
          status: ['SP'],
          description: ['']
        })))
      }),
      vital_signs: this.fb.group({
        notes: [''],
        blood_pressure: [''],
        heart_rate: [''],
        respiratory_rate: [''],
        temperature: [''],
        oxygen_saturation: [''],
        weight: [''],
        height: [''],
        bmi: [''],
        head_circumference: [''],
        glucose: ['']
      }),
      physical_exam: this.fb.group({
        items: this.fb.array(this.regions.map(region => this.fb.group({
          region: [region],
          status: ['normal'],
          description: ['']
        }))),
        glasgow: ['']
      }),
      diagnoses: this.fb.array([this.createDiagnosis(), this.createDiagnosis(), this.createDiagnosis()]),
      treatment_plan: this.fb.group({
        diagnostic_plan: [''],
        pharmacological_prescription: [''],
        diet_care: [''],
        medical_rest: [false],
        rest_days: [''],
        rest_from: [''],
        rest_to: [''],
        alarm_signs: [''],
        next_appointment: ['']
      }),
      professional_data: this.fb.group({
        date: ['', Validators.required],
        start_time: ['', Validators.required],
        end_time: [''],
        doctor_name: ['', Validators.required],
        msp_code: ['']
      })
    });
  }

  private createDiagnosis(): FormGroup {
    return this.fb.group({
      description: [''],
      cie10: [''],
      type: ['PRE']
    });
  }
}
